import {
  ExtraService,
  ExtraServicesByBuildingIdType,
  getBuildingByCheckInOrWorkspaceReservation,
  getBuildingsByWorkday_Timeslots,
  getExtraServices,
  getMeetingById,
  getMeetingRoomEntities,
  LocationType,
  useAppSelector,
  useOverridableFeatureFlags,
} from '@lib/store';
import {useExtraServicesLoader} from './useExtraServicesLoader';
import {useMemo} from 'react';
import {nonEmpty} from '@lib/utils';

export const useExtraServices = (category: ExtraService['category']) => {
  const loadingStatus = useExtraServicesLoader();
  const servicesByBuidlingId = useAppSelector(getExtraServices(category));

  const totalServicesCount = useMemo(
    () => nonEmpty(Object.values(servicesByBuidlingId).flatMap((s) => s?.data)).length,
    [servicesByBuidlingId],
  );

  return {
    loadingStatus,
    servicesByBuidlingId,
    totalServicesCount,
  };
};

export const useGeneralExtraServices = (selectedDate: string) => {
  const {servicesByBuidlingId, loadingStatus, totalServicesCount} = useExtraServices('General');

  const {id: fallbackBuildingId = '', name = ''} =
    useAppSelector((state) => getBuildingByCheckInOrWorkspaceReservation(state, selectedDate)) ?? {};

  const highlightedServices = servicesByBuidlingId[fallbackBuildingId] ?? {
    buildingId: fallbackBuildingId,
    buildingName: name,
    data: [] as ExtraService[],
  };

  return {
    loadingStatus,
    servicesByBuidlingId,
    highlightedServices,
    highlightedServicesCount: highlightedServices.data.length,
    totalServicesCount,
  };
};

export const useWorkdayBasedExtraServices = (selectedDate: string) => {
  const {TimeslotsApi} = useOverridableFeatureFlags();
  const {servicesByBuidlingId, loadingStatus} = useExtraServices('WorkdayBased');

  const {id: fallbackBuildingId = ''} =
    useAppSelector((state) => getBuildingByCheckInOrWorkspaceReservation(state, selectedDate)) ?? {};
  const buildings_timeslots = useAppSelector((state) => getBuildingsByWorkday_Timeslots(state, selectedDate));

  const highlightedServices = servicesByBuidlingId[fallbackBuildingId]?.data ?? [];
  const highlightedServices_timeslots = buildings_timeslots.flatMap(
    (bldg) => servicesByBuidlingId[bldg.id]?.data ?? [],
  );

  return {
    loadingStatus,
    highlightedServices: TimeslotsApi ? highlightedServices_timeslots : highlightedServices,
    highlightedServicesCount: TimeslotsApi ? highlightedServices_timeslots.length : highlightedServices.length,
  };
};

export const useLocationBasedExtraServices = (building: string, locationType: LocationType) => {
  const {servicesByBuidlingId, loadingStatus} = useExtraServices('LocationBased');

  const highlightedServices =
    servicesByBuidlingId[building]?.data.filter(
      (s) => s.category === 'LocationBased' && s.locationTypes.includes(locationType),
    ) ?? [];

  return {
    loadingStatus,
    highlightedServices,
    highlightedServicesCount: highlightedServices.length,
  };
};

export const useEventBasedExtraServices = (meetingId: string) => {
  const {servicesByBuidlingId, loadingStatus} = useExtraServices('EventBased');

  const meetingRoomEntities = useAppSelector(getMeetingRoomEntities);

  const meeting = useAppSelector((state) => getMeetingById(state, meetingId));

  const builingsIds = Array.from(
    new Set(nonEmpty(meeting?.meetingRooms.map((mrId) => meetingRoomEntities[mrId]?.buildingId) ?? [])),
  );

  const highlightedServices: ExtraServicesByBuildingIdType = builingsIds.reduce((acc, bldgId) => {
    const servicesForBuilding = servicesByBuidlingId[bldgId];

    if (!servicesForBuilding) return acc;

    return {...acc, [bldgId]: servicesForBuilding};
  }, {});

  const highlightedServicesCount = useMemo(
    () => nonEmpty(Object.values(highlightedServices).flatMap((s) => s?.data)).length,
    [highlightedServices],
  );

  return {
    highlightedServices,
    loadingStatus,
    highlightedServicesCount,
  };
};
